import React, { FC, memo, ReactElement, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Form, Accordion, Card, Alert, ButtonGroup } from 'react-bootstrap';
import i18next from 'i18next';
import { toDateTime } from '../../../util/date-utils';
import { TrainingPlanTemplateDto, } from '../../../../../shared/dtos/trainingPlanTemplate.dto';
import { useExtendedAuth } from '../../../util/auth0';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import { TrainingDto, EnduranceTrainingDto } from "../../../../../shared/dtos/trainingplan.dto";

type LoadTemplateModalProps = {
  show: boolean;
  onPositive: (selectedTrainings: TrainingDto[], selectedEnduranceTrainings: EnduranceTrainingDto[]) => void;
  onNegative: () => void;
  trainingIndex: number;
  maxTrainingCount: number;
};

const LoadTemplateModal: FC<LoadTemplateModalProps> = ({ show, onPositive, onNegative, trainingIndex, maxTrainingCount }: LoadTemplateModalProps) : ReactElement => {
  const { isAdminUser } = useExtendedAuth();
  const { authFetch } = useAuthFetchFromRestAPI();

  const [activeKey, setActiveKey] = useState<string | null>('0');
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isGlobal, setIsGlobal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TrainingPlanTemplateDto | null>(null);
  const [selectedTrainings, setSelectedTrainings] = useState<TrainingDto[]>([]);
  const [selectedEnduranceTrainings, setSelectedEnduranceTrainings] = useState<EnduranceTrainingDto[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [templateList, setTemplateList] = useState<TrainingPlanTemplateDto[]>([]);

  useEffect(() => {
    if (show) {
      resetModalState();
    }
  }, [show]);

  useEffect(() => {
    void loadTemplates(isGlobal);
  }, [isGlobal]);

  useEffect(() => {
    const checkIsAdminUser = async () => {
      const isAdmin = await isAdminUser();
      setIsAdmin(isAdmin);
    };

    void checkIsAdminUser();
  }, [isAdminUser]);

  const loadTemplates = async (isGlobal = false): Promise<void> => {
    setTemplateList([]);

    try {
      const templates: TrainingPlanTemplateDto[] = await authFetch(isGlobal ? '/api/v1/template/list' : '/api/v1/template/list/therapist');
      setTemplateList(templates);
      
    } catch (e) {
      console.error(e);
    }
  };

  const deleteTemplate = async (): Promise<void> => {
    if(!selectedTemplate) {
      return;
    }
    
    await authFetch(`/api/v1/template/${selectedTemplate.id}`, { method: 'DELETE' });
    await loadTemplates(isGlobal);

    setShowConfirmDelete(false);
  };

  const resetModalState = (): void => {
    setSelectedTrainings([]);
    setSelectedEnduranceTrainings([]);
    setActiveKey('0');
    setSelectedTemplate(null);
    setShowConfirmDelete(false);
  };

  const renderTrainingSelection = (training: TrainingDto, index: number) => {
    const checked = selectedTrainings.some(x => x.id === training.id);

    return (
      <li className="list-group-item" key={`Training-${training.id}`}>
        <Form.Check type="checkbox" id={`training-${index}`}>
          <Form.Check.Input
            className="mt-2"
            type="checkbox"
            checked={checked}
            onChange={(e) => {
              setSelectedTrainings(prev =>
                e.target.checked ? checked ? prev : [...prev, training] : prev.filter(x => x.id !== training.id)
              );
            }}
          />
          <Form.Check.Label>{training.name}</Form.Check.Label>
        </Form.Check>
      </li>
    );
  };

  const renderEnduranceTrainingSelection = (training: EnduranceTrainingDto, index: number) => {
    const checked = selectedEnduranceTrainings.some(x => x.id === training.id);

    return (
      <li className="list-group-item" key={`EnduranceTraining-${training.id}`}>
        <Form.Check type="checkbox" id={`endurance-training-${index}`}>
          <Form.Check.Input
            className="mt-2"
            type="checkbox"
            checked={checked}
            onChange={(e) => {
              setSelectedEnduranceTrainings(prev =>
                e.target.checked ? checked ? prev : [...prev, training] : prev.filter(x => x.id !== training.id)
              );
            }}
          />
          <Form.Check.Label>{training.name}</Form.Check.Label>
        </Form.Check>
      </li>
    );
  };

  const renderTemplate = (template: TrainingPlanTemplateDto, index: number) => {
    const key = index.toString();
    const isOpen = activeKey === key;

    return (
      <Card key={`Template-${index}`}>
        <Card.Header className="d-flex align-items-center">
          <Button
            className="text-decoration-none"
            variant="link"
            onClick={() => setActiveKey(prev => (prev === key ? null : key))}
          >
            <span className="me-2">
              {isOpen ? <i className="fas fa-caret-up" /> : <i className="fas fa-caret-down" />}
            </span>
            {template.templateName}
          </Button>

          <div className="d-flex align-items-center ms-auto">
            <small className="pe-3">{toDateTime(template.createdAt).toRelative()}</small>

            {((isAdmin && isGlobal) || !isGlobal) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTemplate(template);
                  setShowConfirmDelete(true);
                }}
                style={{ fontSize: '1rem' }}
              >
                <i className="fas fa-trash" />
              </Button>
            )}
          </div>
        </Card.Header>

        <Accordion.Collapse eventKey={key}>
          <Card.Body>
            <ul className="list-group">
              {template.trainings.map((training, index) =>
                renderTrainingSelection(training, index)
              )}
              {(template.enduranceTrainings ?? []).map((training, index) =>
                renderEnduranceTrainingSelection(training, index)
              )}
            </ul>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    );
  };

  const hasTooManyTrainings = selectedTrainings.length + selectedEnduranceTrainings.length + trainingIndex > maxTrainingCount;

  return (
    <Modal size="lg" show={show} onHide={onNegative}>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          <h2>{i18next.t('trainingPlanForm.templateModalTitle')}</h2>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>

        {selectedTemplate && (
          <ConfirmationModal
            show={showConfirmDelete}
            title={i18next.t('trainingPlanForm.deleteTemplateTitle')}
            body={i18next.t('trainingPlanForm.deleteTemplateBody').replace('{templateName}', selectedTemplate.templateName)}
            buttonPositive={i18next.t('generic.delete')}
            buttonNegative={i18next.t('generic.close')}
            onPositive={deleteTemplate}
            onNegative={() => {
              setShowConfirmDelete(false);
              setSelectedTemplate(null);
            }}
          />
        )}

        <ButtonGroup aria-label="GlobalTemplate" className="pb-2">
          <Button
            variant={!isGlobal ? 'primary' : 'secondary'}
            onClick={() => setIsGlobal(false)}
          >
            {i18next.t('trainingPlanForm.yourTemplateText')}
          </Button>

          <Button
            variant={isGlobal ? 'primary' : 'secondary'}
            onClick={() => setIsGlobal(true)}
          >
            {i18next.t('trainingPlanForm.globalTemplateText')}
          </Button>
        </ButtonGroup>

        {templateList.length > 0 ? (
          <>
            {hasTooManyTrainings && (
              <Alert key={`alert-${selectedTrainings.length}`} variant="danger">
                {i18next.t('trainingPlanForm.templateModalError')}
              </Alert>
            )}

            <Accordion activeKey={activeKey}>
              <div className="overflow-auto" style={{ maxHeight: '50vh' }}>
                {templateList.map(renderTemplate)}
              </div>
            </Accordion>
          </>
        ) : (
          <p>{i18next.t('trainingPlanForm.templateModalNoTemplate')}</p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          style={{ marginRight: 'auto' }}
          onClick={onNegative}
        >
          {i18next.t('generic.cancel')}
        </Button>

        <Button
          onClick={() => onPositive(selectedTrainings, selectedEnduranceTrainings)}
          variant="danger"
          disabled={hasTooManyTrainings}
        >
          {i18next.t('trainingPlanForm.templateModalButton')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(LoadTemplateModal);
